import React, { useEffect, useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  useMediaQuery,
  useTheme,
  Typography,
} from "@mui/material";
import Dvr from "@mui/icons-material/Dvr";
import FeedbackIcon from "@mui/icons-material/Feedback";
import LibraryBooks from "@mui/icons-material/LibraryBooks";
import ComplianceIcon from "@mui/icons-material/Policy";
import AttendanceIcon from "@mui/icons-material/CalendarToday";
import SkillsIcon from "@mui/icons-material/EmojiObjects";
import Badge from "@mui/icons-material/Badge";
import GroupsIcon from "@mui/icons-material/Groups";
import StarsIcon from "@mui/icons-material/Stars";
import SegmentIcon from "@mui/icons-material/Segment";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import VerifiedIcon from "@mui/icons-material/Verified";
import MilitaryTech from "@mui/icons-material/MilitaryTech";
import KeyboardArrowRightOutlinedIcon from "@mui/icons-material/KeyboardArrowRightOutlined";
import HistoryIcon from "@mui/icons-material/History";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import { getSidebarItems } from "../Data/data";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/Store";
import { setSelectedRole } from "../../store/AuthSlice"; // Correct path to AuthSlice.tsx
import { toggleSidebar, openSidebar, closeSidebar } from "../../store/SidebarSlice";

interface SidebarItem {
  label: string;
  route: string;
  children?: SidebarItem[];
}

const drawerWidth = 250;
const NAVBAR_HEIGHT = 64;
const FOOTER_HEIGHT = 58;

const SidebarDemo: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
const isOpen = useSelector((state: RootState) => state.sidebar.isOpen);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Fetch roles from Redux store
  const roles = useSelector((state: RootState) => state.auth.roles);
  const selectedRole = useSelector((state: RootState) => state.auth.selectedRole);

  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});
  const sidebarItems = getSidebarItems([selectedRole]);

  // Handle sidebar item click
  const handleItemClick = (item: SidebarItem, depth: number) => {
    const hasChildren = item.children && item.children.length > 0;
    if (!hasChildren) {
      navigate(`/${item.route}`);
      if (isMobile) toggleSidebar();
    } else {
      setExpandedItems((prev) => ({
        ...prev,
        [item.label]: !prev[item.label],
      }));
    }
  };

  const getIcon = (label: string) => {
    switch (label) {
      case "Dashboard":
        return <Dvr />;
      // case "Feedback": // COMMENTED OUT - Feedback section hidden
      //   return <FeedbackIcon />;
      case "Reports":
        return <LibraryBooks />;
      case "Compliance":
        return <ComplianceIcon />;
      case "Attendance":
        return <AttendanceIcon />;
      case "Skills":
        return <SkillsIcon />;
      case "Manager History":
        return <HistoryIcon />;
      case "Employee Information":
        return <Badge />;
      case "PMS":
        return <MilitaryTech />;
      case "Peer Review":
        return <Diversity3Icon />;
      case "Client Feedback":
        return <FeedbackIcon />;
      case "Create team":
        return <GroupsIcon />;
      case "Projects":
        return <WorkspacesIcon />;
      // case "Ranking": // COMMENTED OUT - Ranking functionality hidden
      //   return <StarsIcon />;
      // case "Forms": // COMMENTED OUT - Forms functionality hidden
      //   return <SegmentIcon />;
      // case "Subordinate": // COMMENTED OUT - Subordinate functionality hidden
      //   return <WorkspacesIcon />;
      // case "Permission": // COMMENTED OUT - Permission functionality hidden
      //   return <VerifiedIcon />;
      default:
        return <Dvr />;
    }
  };

  const renderSidebarItem = (item: SidebarItem, index: number, depth: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.label] || false;

    return (
      <React.Fragment key={`${item.label}-${index}-${depth}`}>
        <ListItem
          button
          onClick={() => handleItemClick(item, depth)}
          sx={{
            pl: 2 + depth * 2,
            backgroundColor: depth > 0 ? "rgba(255,255,255,0.1)" : "inherit",
            "&:hover": { backgroundColor: "#d0f0d0" },
            transition: "background-color 0.3s",
          }}
        >
          {depth === 0 ? (
            <ListItemIcon sx={{ color: "#2e7d32", minWidth: 32 }}>{getIcon(item.label)}</ListItemIcon>
          ) : (
            <ListItemIcon sx={{ color: "#2e7d32", minWidth: 24 }}>
              <KeyboardArrowRightOutlinedIcon sx={{ fontSize: 18 }} />
            </ListItemIcon>
          )}
          <ListItemText
            primary={<Typography sx={{ color: "#2e7d32", fontWeight: 500 }}>{item.label}</Typography>}
          />
          {hasChildren && (
            <Typography sx={{ fontWeight: "bold", fontSize: 18, color: "#2e7d32" }}>
              {isExpanded ? "−" : "+"}
            </Typography>
          )}
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children?.map((child, childIndex) =>
                renderSidebarItem(child, childIndex, depth + 1)
              )}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      open={isOpen}
      onClose={() => dispatch(toggleSidebar())}
      anchor="left"
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundColor: "#e8f5e9",
          top: isMobile ? 0 : `${NAVBAR_HEIGHT}px`,
          height: isMobile ? "100vh" : `calc(100% - ${NAVBAR_HEIGHT + FOOTER_HEIGHT}px)`,
          zIndex: isMobile ? theme.zIndex.drawer + 1 : theme.zIndex.appBar - 2,
          transition: "all 0.3s",
          overflowY: "auto",
        },
      }}
    >
      <List>
        {sidebarItems.map((item, index) => renderSidebarItem(item, index))}
      </List>
    </Drawer>
  );
};

export default SidebarDemo;
